import React, { useState, useMemo } from "react";
import {
  CreditCard,
  RefreshCw,
  Wallet,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useGetMyFinanceSummaryQuery } from "../api/billingApi";
import DuesTable from "../components/DuesTable";

export type LedgerItem = {
  id: number;
  name: string;
  type: "one_time" | "annual";
  year: number | null;
  base_amount: number;
  balance: number;
  already_paid: boolean;
  currency: string;
};

function StatCard({
  title,
  value,
  icon: Icon,
  variant = "default",
}: {
  title: string;
  value: string;
  icon: any;
  variant?: "default" | "warning" | "success";
}) {
  const bgStyles = {
    default: "bg-white border-gray-200",
    warning: "bg-amber-50 border-amber-100",
    success: "bg-green-50 border-green-100",
  };

  return (
    <div className={`border rounded-[24px] p-6 shadow-sm ${bgStyles[variant]}`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-1">
            {title}
          </div>
          <div className="text-2xl font-black text-gray-900 tracking-tight">
            {value}
          </div>
        </div>

        <div className="p-2 rounded-xl bg-gray-50">
          <Icon className="w-5 h-5 text-gray-600" />
        </div>
      </div>
    </div>
  );
}

export default function MyDuesPage() {
  const [year, setYear] = useState<string>("all");

  const { data, isLoading, isFetching, refetch } =
    useGetMyFinanceSummaryQuery();
  console.log(data, "tjis is what");
  /**
   * ONE TIME CHARGES
   */
  const oneTimeItems = useMemo<LedgerItem[]>(() => {
    if (!data?.one_time?.charges) return [];

    return data.one_time.charges.map((x: any) => ({
      id: x.charge_price_id,
      name: x.charge_name,
      type: "one_time",
      year: null,
      base_amount: x.amount ?? 0,
      balance: x.balance ?? 0,
      already_paid: x.status === "paid",
      currency: "NGN",
    }));
  }, [data]);

  /**
   * ANNUAL CHARGES
   */
  const annualItems = useMemo<LedgerItem[]>(() => {
    if (!data?.annual) return [];

    const flattened = data.annual.flatMap((yearGroup: any) =>
      (yearGroup.charges ?? []).map((charge: any) => ({
        id: charge.charge_price_id,
        name: charge.charge_name,
        type: "annual",
        year: yearGroup.year,
        base_amount: charge.amount ?? 0,
        balance: charge.balance ?? 0,
        already_paid: charge.status === "paid",
        currency: "NGN",
      }))
    );

    if (year === "all") return flattened;

    return flattened.filter((x) => x.year === Number(year));
  }, [data, year]);

  /**
   * YEARS DROPDOWN
   */

  const availableYears = useMemo<number[]>(() => {
    if (!data?.annual) return [];

    const years = data.annual.map((x: any) => Number(x.year)); // Ensure it's a number
    return Array.from(new Set(years)).sort((a, b) => b - a);
  }, [data]);
  /**
   * MERGED ITEMS (ONLY FOR COUNTING)
   */
  const allItems = useMemo(() => {
    return [...oneTimeItems, ...annualItems];
  }, [oneTimeItems, annualItems]);

  const paidCount = allItems.filter((i) => i.already_paid).length;

  /**
   * GRAND TOTAL (NEVER FILTERED)
   */
  const totalOutstanding =
    data?.grand_summary?.total_owed ?? data?.grand_summary?.total_owed ?? 0;

  if (isLoading) {
    return <div className="p-10">Loading financial data...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* HEADER */}

      <div className="bg-white border border-gray-200 rounded-[32px] p-6 shadow-sm">
        <div className="flex justify-between">
          <div className="flex gap-4 items-center">
            <div className="p-3 bg-gray-900 rounded-2xl">
              <CreditCard className="w-6 h-6 text-white" />
            </div>

            <div>
              <h1 className="text-2xl font-black">Financial Overview</h1>
              <p className="text-sm text-gray-500">
                One-time & annual membership charges
              </p>
            </div>
          </div>

          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 border rounded-xl"
          >
            <RefreshCw
              className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* SUMMARY */}

      <div className="grid grid-cols-3 gap-6">
        <StatCard
          title="Total Outstanding"
          value={`₦${totalOutstanding.toLocaleString()}`}
          icon={Wallet}
          variant="warning"
        />

        <StatCard
          title="Payments Cleared"
          value={paidCount.toString()}
          icon={CheckCircle2}
          variant="success"
        />

        <StatCard
          title="Active Charges"
          value={allItems.length.toString()}
          icon={AlertCircle}
        />
      </div>

      {/* ONE TIME */}

      <div className="bg-white border rounded-[32px] overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 font-bold">One-Time Charges</div>

        <DuesTable items={oneTimeItems} />
      </div>

      {/* ANNUAL */}

      <div className="bg-white border rounded-[32px] overflow-hidden">
        <div className="flex justify-between px-6 py-4 bg-gray-50">
          <div className="font-bold">Annual Charges</div>

          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="border rounded-xl px-3 py-2"
          >
            <option value="all">All Time</option>

            {availableYears.map((y) => (
              <option key={y} value={y.toString()}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <DuesTable items={annualItems} />
      </div>
    </div>
  );
}
